import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Building2, Users, Bell, Home, Menu } from 'lucide-react';
import { toast } from 'react-toastify';
import logoutManager from '../../logoutManager';
import api from '../../api';

const Header = ({ parentData, notifications = [], orgInfo = {} }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // State for institution info
  const [institutionName, setInstitutionName] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  // Load institution info from props or localStorage
  useEffect(() => {
    console.log('[ParentHeader] Loading institution info...');
    
    // Priority 1: Use orgInfo from props
    if (orgInfo?.name) {
      console.log('[ParentHeader] Using orgInfo from props:', orgInfo.name);
      setInstitutionName(orgInfo.name);
      setLogoUrl(orgInfo.logoUrl || null);
      
      const id = localStorage.getItem('tenantId');
      if (id) setTenantId(id);
      return;
    }

    // Priority 2: Try localStorage
    try {
      const t = JSON.parse(localStorage.getItem('tenantData') || '{}');
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      
      let id = localStorage.getItem('tenantId');
      if (!id) id = u.tenantId || u.organizationId || t.institutionId || t.id;
      if (id) setTenantId(id);

      const name = t.institutionName || t.name || '';
      if (name) {
        setInstitutionName(name);
      } else {
        setInstitutionName('School ERP');
      }
      
      if (t.logoUrl || t.logo) {
        setLogoUrl(t.logoUrl || t.logo);
      }
    } catch (err) {
      console.error('❌ [ParentHeader] Storage parse error:', err);
      setInstitutionName('School ERP');
    }
  }, [orgInfo]);

  // Fetch org info from API if not available
  useEffect(() => {
    const fetchOrgInfo = async () => {
      if (orgInfo?.name || institutionName !== 'School ERP') {
        return;
      }

      try {
        const tenantId = localStorage.getItem('tenantId');
        const token = localStorage.getItem('token');
        
        if (!tenantId) return;

        console.log('[ParentHeader] Fetching org info from API...');
        const response = await api.get(`/api/organization/basic-info/${tenantId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.data && response.data.success) {
          const name = response.data.name || '';
          const logo = response.data.logoUrl || '';
          
          if (name) {
            setInstitutionName(name);
          }
          if (logo) {
            setLogoUrl(logo);
          }
          
          const existing = JSON.parse(localStorage.getItem('tenantData') || '{}');
          const updated = {
            ...existing,
            institutionName: name || existing.institutionName || 'School ERP',
            logoUrl: logo || existing.logoUrl || ''
          };
          localStorage.setItem('tenantData', JSON.stringify(updated));
        }
      } catch (err) {
        console.error('❌ [ParentHeader] Error fetching org info:', err);
      }
    };

    fetchOrgInfo();
  }, [orgInfo, institutionName]);

  // Update unread count
  useEffect(() => {
    if (notifications && Array.isArray(notifications)) {
      const unread = notifications.filter(n => !n.isRead).length;
      setUnreadCount(unread);
    }
  }, [notifications]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setShowLogoutConfirm(false);
    setShowMenu(false);
    
    const redirectPath = '/';

    try {
      await logoutManager.logout({
        redirectTo: redirectPath,
        showToast: true,
        onSuccess: () => {
          console.log('✅ Parent logged out successfully');
        }
      });
    } catch (error) {
      localStorage.clear();
      window.location.href = '/';
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleHome = () => {
    navigate('/parent/dashboard');
  };



  const displayName = institutionName || 'School ERP';
  const hasValidLogo = !logoError && logoUrl && String(logoUrl).trim() !== '';
  const profileName = parentData?.parentName || parentData?.name || 'Parent';

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 shadow-xs flex flex-col">
        {/* Accent Bar */}
        <div className="h-0.5 w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shrink-0" />

        {/* Navbar */}
        <div className="w-full px-3 sm:px-6 h-14 flex items-center justify-between gap-3">
          
          {/* Identity: Logo & Institution Name */}
          <div 
            onClick={handleHome}
            className="flex items-center gap-2.5 min-w-0 max-w-[75%] cursor-pointer group"
          >
            <div className="shrink-0">
              {hasValidLogo ? (
                <div className="h-9 w-9 rounded-xl border border-slate-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-900 flex items-center justify-center shadow-xs overflow-hidden">
                  <img
                    src={logoUrl}
                    alt={displayName}
                    className="h-full w-full object-contain rounded-lg"
                    onError={() => {
                      console.error('❌ [ParentHeader] Image failed to load:', logoUrl);
                      setLogoError(true);
                    }}
                  />
                </div>
              ) : (
                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center text-white shadow-xs font-black text-sm">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="flex flex-col min-w-0 leading-none">
              <h1 className="text-xs sm:text-sm font-black text-slate-900 dark:text-white truncate tracking-tight group-hover:text-indigo-600 transition-colors">
                {displayName}
              </h1>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[9px] sm:text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                  Parent Portal
                </span>
                <span className="text-slate-300 dark:text-gray-600 text-[10px] hidden sm:inline">•</span>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium hidden sm:inline truncate">
                  Truyon ERP
                </span>
              </div>
            </div>
          </div>

          {/* Right Side: Actions */}
          <div className="shrink-0 flex items-center gap-2">
            
            {/* Home Button */}
            <button
              onClick={handleHome}
              className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 bg-slate-100/80 dark:bg-gray-700/60 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-gray-600 font-medium hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors"
              title="Dashboard"
            >
              <Home size={14} className="text-slate-400" />
              <span className="hidden md:inline">Dashboard</span>
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowMenu(false);
                }}
                className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800"
                title="Notifications"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-sm border-2 border-white dark:border-gray-800">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-slate-100 dark:border-gray-700 py-1.5 z-50">
                  <div className="px-4 py-2.5 border-b border-slate-50 dark:border-gray-700 flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-900 dark:text-white">Notifications</p>
                    {unreadCount > 0 && (
                      <span className="text-[10px] font-extrabold text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded-md">
                        {unreadCount} New
                      </span>
                    )}
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200">All caught up!</p>
                        <p className="text-[11px] text-slate-400">No new notifications.</p>
                      </div>
                    ) : (
                      notifications.map((notif, index) => (
                        <div key={index} className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-700/50 cursor-pointer border-b border-slate-50 dark:border-gray-700 last:border-0 transition-colors">
                          <p className="text-xs font-bold text-slate-800 dark:text-white">{notif.title || 'Update'}</p>
                          <p className="text-[11px] text-slate-400 dark:text-slate-400 font-medium mt-0.5">{notif.message || ''}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 bg-slate-100/80 dark:bg-gray-700/60 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-gray-600 font-medium">
              <User size={12} className="text-slate-400" />
              <span className="max-w-[100px] truncate">{profileName}</span>
            </div>

            {/* ✅ Direct Logout Button */}
            <button
              onClick={() => setShowLogoutConfirm(true)}
              disabled={isLoggingOut}
              className="flex items-center justify-center gap-1.5 h-8 px-2.5 sm:px-3 bg-slate-50 dark:bg-rose-900/20 hover:bg-rose-50 dark:hover:bg-rose-900/40 text-slate-700 dark:text-rose-300 hover:text-rose-600 border border-slate-200 dark:border-rose-800/60 rounded-lg transition-colors disabled:opacity-60 text-xs font-bold"
            >
              {isLoggingOut ? (
                <svg className="animate-spin h-3.5 w-3.5 text-rose-600" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" />
                </svg>
              ) : (
                <>
                  <span className="hidden xs:inline">Logout</span>
                  <LogOut size={13} />
                </>
              )}
            </button>

            {/* Menu Button (Mobile) */}
            <button
              onClick={() => {
                setShowMenu(!showMenu);
                setShowNotifications(false);
              }}
              className="sm:hidden p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>

        {/* Sub-strip with Institution ID */}
        {tenantId && (
          <div className="border-t border-slate-100 dark:border-gray-700/60 bg-slate-50/60 dark:bg-gray-900/40 px-3 sm:px-6 py-1 flex items-center justify-between gap-4 text-[10px] text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1 min-w-0">
              <Building2 size={10} className="text-slate-400 shrink-0" />
              <p className="truncate">
                Institution ID: <span className="text-slate-800 dark:text-slate-200 font-bold">{tenantId}</span>
              </p>
            </div>
            <div className="flex items-center gap-1 min-w-0">
              <Users size={10} className="text-slate-400 shrink-0" />
              <p className="truncate">
                Parent: <span className="text-slate-800 dark:text-slate-200 font-bold">{profileName}</span>
              </p>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu Dropdown */}
      {showMenu && (
        <div className="sm:hidden absolute right-4 top-14 mt-2 w-48 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-slate-100 dark:border-gray-700 py-1.5 z-50">
          <div className="px-4 py-2 border-b border-slate-50 dark:border-gray-700">
            <p className="text-xs font-bold text-slate-800 dark:text-white truncate">{profileName}</p>
            <p className="text-[10px] text-slate-400 truncate">{parentData?.parentEmail || ''}</p>
          </div>
          
          <button
            onClick={handleHome}
            className="w-full px-4 py-2 text-left text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
          >
            <Home size={16} className="text-slate-400" />
            Dashboard
          </button>
          
          <button
            onClick={() => {
              setShowMenu(false);
              toast.info('Settings coming soon');
            }}
            className="w-full px-4 py-2 text-left text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 flex items-center gap-2.5 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </button>

          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full px-4 py-2 text-left text-xs font-bold text-rose-600 hover:bg-rose-50/60 flex items-center gap-2.5 border-t border-slate-50 dark:border-gray-700 mt-1 transition-colors"
          >
            <LogOut size={16} className="text-rose-500" />
            Logout
          </button>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[1000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-xs w-full shadow-2xl border border-slate-100 dark:border-gray-700 p-5 text-center">
            <div className="w-12 h-12 bg-rose-50 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-3 overflow-hidden p-1">
              <LogOut size={20} className="text-rose-600 dark:text-rose-400" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Confirm Logout</h3>
            <p className="text-slate-500 dark:text-slate-400 text-xs mb-4">
              Are you sure you want to exit your parent portal session for <strong className="text-slate-800 dark:text-slate-200">{displayName}</strong>?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
                className="flex-1 py-2 text-xs font-bold bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex-1 py-2 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center space-x-1"
              >
                {isLoggingOut ? 'Exiting...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;